import { useMutation } from '@apollo/client'
import InstagramIcon from '@mui/icons-material/Instagram'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type FormEvent, useMemo, useState } from 'react'
import { useLocale, useNotify, useTranslate } from 'react-admin'
import {
  type InstagramAccount as Account,
  DISCONNECT,
  type InstagramPost,
  loginUrl,
  redirectUri,
  SAVE_APP,
} from './instagram'

/**
 * The Instagram account the CMS publishes to: the Meta app's id and secret
 * (entered once, the secret stored encrypted by the feed service), then
 * "Mit Instagram verbinden" — Instagram's own login, which comes back to
 * /instagram (InstagramCallback). The secret travels as a GraphQL variable:
 * Hasura logs query text outside development, never variables.
 */
export const InstagramAccount = ({
  account,
  posts,
  expired,
  onChanged,
}: {
  account: Account | null
  posts: InstagramPost[]
  expired: boolean
  onChanged: () => void
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const locale = useLocale()
  const [editing, setEditing] = useState(false)
  const [appId, setAppId] = useState('')
  const [appSecret, setAppSecret] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saveApp, { loading: saving }] = useMutation(SAVE_APP)
  const [disconnect, { loading: disconnecting }] = useMutation(DISCONNECT)
  const date = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }), [locale])
  const dateTime = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale]
  )

  const showForm = !account || editing

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await saveApp({ variables: { app_id: appId.trim(), app_secret: appSecret.trim() } })
    } catch (err: any) {
      setError(err.message)
      return
    }
    setAppSecret('')
    setEditing(false)
    notify('social.instagram.saved', { type: 'info' })
    onChanged()
  }

  const drop = async () => {
    setError(null)
    try {
      await disconnect()
      notify('social.instagram.disconnected', { type: 'info' })
    } catch (err: any) {
      setError(err.message)
    }
    onChanged()
  }

  const connected = !!account?.username && !expired

  return (
    <Card variant='outlined' id='instagram' data-testid='instagram-account'>
      <CardContent>
        <Stack direction='row' spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
          <InstagramIcon />
          <Typography variant='h6'>{translate('social.instagram.title')}</Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2, maxWidth: 820 }}>
          {translate('social.instagram.intro')}
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {showForm && (
          <>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              {translate('social.instagram.setup')}
            </Typography>
            <Box component='ol' sx={{ mt: 0, mb: 2, pl: 3, maxWidth: 820, typography: 'body2' }}>
              <li>{translate('social.instagram.step_account')}</li>
              <li>{translate('social.instagram.step_app')}</li>
              <li>
                {translate('social.instagram.step_redirect')}{' '}
                <Box
                  component='code'
                  data-testid='instagram-redirect'
                  sx={{
                    px: 0.5,
                    bgcolor: 'action.hover',
                    borderRadius: 0.5,
                    wordBreak: 'break-all',
                  }}
                >
                  {redirectUri()}
                </Box>
              </li>
              <li>{translate('social.instagram.step_tester')}</li>
              <li>{translate('social.instagram.step_keys')}</li>
            </Box>
            <Box component='form' onSubmit={submit} autoComplete='off'>
              <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    size='small'
                    name='instagram-app-id'
                    label={translate('social.instagram.app_id')}
                    value={appId}
                    onChange={(e) => setAppId(e.target.value)}
                    slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    fullWidth
                    size='small'
                    type='password'
                    name='instagram-app-secret'
                    label={translate('social.instagram.app_secret')}
                    value={appSecret}
                    onChange={(e) => setAppSecret(e.target.value)}
                    slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Stack direction='row' spacing={1}>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={saving || !appId.trim() || !appSecret.trim()}
                    >
                      {translate('social.instagram.save')}
                    </Button>
                    {account && (
                      <Button onClick={() => setEditing(false)}>
                        {translate('social.cancel')}
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {!showForm && account && (
          <>
            {connected ? (
              <Alert severity='success' sx={{ mb: 1.5 }} data-testid='instagram-connected'>
                {translate('social.instagram.connected', {
                  username: account.username,
                  since: date.format(new Date(account.connected_at ?? Date.now())),
                  until: date.format(new Date(account.token_expires_at ?? Date.now())),
                })}
              </Alert>
            ) : (
              <>
                {expired && account.username && (
                  <Alert severity='warning' sx={{ mb: 1.5 }}>
                    {translate('social.instagram.expired', { username: account.username })}
                  </Alert>
                )}
                <Typography variant='body2' sx={{ mb: 1.5 }}>
                  {translate('social.instagram.app_stored', { app_id: account.app_id })}
                </Typography>
              </>
            )}
            {account.last_error && (
              <Alert severity='warning' sx={{ mb: 1.5 }}>
                {translate('social.instagram.refresh_failed', { message: account.last_error })}
              </Alert>
            )}
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {!connected && (
                <Button
                  variant='contained'
                  startIcon={<InstagramIcon />}
                  // The state is made on the click: one login, one state.
                  onClick={() => window.location.assign(loginUrl(account.app_id))}
                  data-testid='instagram-login'
                >
                  {translate('social.instagram.connect')}
                </Button>
              )}
              {connected && (
                <Button variant='outlined' disabled={disconnecting} onClick={drop}>
                  {translate('social.instagram.disconnect')}
                </Button>
              )}
              <Button
                onClick={() => {
                  setAppId(account.app_id)
                  setAppSecret('')
                  setEditing(true)
                }}
              >
                {translate('social.instagram.change_app')}
              </Button>
            </Stack>
          </>
        )}

        {posts.length > 0 && (
          <Box sx={{ mt: 2 }} data-testid='instagram-posts'>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              {translate('social.instagram.recent')}
            </Typography>
            {posts.map((p) => (
              <Typography key={p.id} variant='body2' color={p.ok ? 'text.primary' : 'error'}>
                {dateTime.format(new Date(p.created_at))} ·{' '}
                {translate(`social.instagram.kinds.${p.kind}`)}
                {p.boat?.name ? ` · ${p.boat.name}` : ''} · {p.message}
                {p.permalink && (
                  <>
                    {' '}
                    <MuiLink href={p.permalink} target='_blank' rel='noopener'>
                      {translate('social.view_post')}
                    </MuiLink>
                  </>
                )}
              </Typography>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default InstagramAccount
